import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip as LeafletTooltip, useMap } from 'react-leaflet'
import type { Recommendation, POI } from '../../lib/types'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../ui'
import { ChartProvenanceCallout } from '../ChartProvenanceCallout'
import {
  Compass,
  ExternalLink,
  MapPin,
  Sparkles,
  Mountain,
  Utensils,
  Home as HomeIcon,
  CheckCircle2,
  ZoomIn,
  BarChart3,
  Building2,
  PieChart as PieChartIcon,
  TrendingUp,
  Award,
  ShieldCheck,
  Star,
  Target,
  Flame,
  Layers,
  Globe2,
} from 'lucide-react'

// Paleta temática para sectores de Levi
const SECTOR_THEME: Record<string, { color: string; glow: string; label: string; icon: any }> = {
  restaurant: {
    color: '#fb923c', // Naranja fuego / gastronomía
    glow: 'rgba(251, 146, 60, 0.45)',
    label: 'Restauración & Gastronomía',
    icon: Utensils,
  },
  chalet: {
    color: '#38bdf8', // Azul hielo / alojamiento
    glow: 'rgba(56, 189, 248, 0.45)',
    label: 'Cabañas & Glass Igloos',
    icon: HomeIcon,
  },
  attraction: {
    color: '#c084fc', // Violeta aurora / experiencias
    glow: 'rgba(192, 132, 252, 0.45)',
    label: 'Safaris & Actividades',
    icon: Mountain,
  },
  default: {
    color: '#2dd4bf', // Turquesa polar
    glow: 'rgba(45, 212, 191, 0.45)',
    label: 'Experiencia Turística',
    icon: Sparkles,
  },
}

// Coordenadas del epicentro de Levi Keskus (Front Slopes)
const LEVI_CENTER: [number, number] = [67.8045, 24.8082]

// Componente para sincronizar vista Leaflet cuando se selecciona un POI
function MapFocusHandler({ selectedPoi }: { selectedPoi?: POI | null }) {
  const map = useMap()
  useEffect(() => {
    if (selectedPoi && selectedPoi.latitude && selectedPoi.longitude) {
      map.flyTo([selectedPoi.latitude, selectedPoi.longitude], 15, { duration: 1.2 })
    }
  }, [selectedPoi, map])
  return null
}

interface LeviPoiMapProps {
  recommendations: Recommendation[]
  selectedPoiId?: string | null
  onSelectPoi?: (poi: POI) => void
  poiTotal?: number
  poiSmeCount?: number
}

export function LeviPoiMap({ recommendations, selectedPoiId, onSelectPoi, poiTotal, poiSmeCount }: LeviPoiMapProps) {
  const { t } = useTranslation()
  const [activeLayer, setActiveLayer] = useState<'all' | 'heatmap' | 'markers'>('all')

  const pois = useMemo(() => recommendations.map((r) => r.poi), [recommendations])

  const selectedPoi = useMemo(() => {
    return pois.find((p) => p.poi_id === selectedPoiId)
  }, [pois, selectedPoiId])

  const selectedRec = useMemo(() => {
    return recommendations.find((r) => r.poi.poi_id === selectedPoiId)
  }, [recommendations, selectedPoiId])

  // --- CÁLCULOS DE INTELIGENCIA DE MERCADO DE VALOR AÑADIDO ---
  const marketStats = useMemo(() => {
    const total = pois.length || 1
    // El KPI de PYMEs se basa en el total REAL de Levi (poiTotal/poiSmeCount
    // del motor) cuando esta disponible; si no, cae a la lista mostrada.
    const smeTotal = poiTotal && poiTotal > 0 ? poiTotal : total
    const smeCount =
      poiSmeCount != null && poiSmeCount > 0
        ? poiSmeCount
        : pois.filter((p) => p.is_sme).length
    const smePct = Math.round((smeCount / smeTotal) * 100)
    const coreCount = pois.filter((p) => p.in_levi_core).length
    const corePct = Math.round((coreCount / total) * 100)

    const sumScore = recommendations.reduce((acc, r) => acc + r.score, 0)
    const avgScore = (sumScore / (recommendations.length || 1)).toFixed(2)

    // Negocios con presencia digital (sitio web propio)
    const withWebCount = pois.filter((p) => Boolean(p.website)).length
    const digitalReadinessPct = Math.round((withWebCount / total) * 100)

    // Agrupación y rendimiento por sector
    const catMap: Record<string, { count: number; totalScore: number }> = {}
    recommendations.forEach((r) => {
      const cat = r.poi.category || r.poi.industry || 'other'
      if (!catMap[cat]) catMap[cat] = { count: 0, totalScore: 0 }
      catMap[cat].count++
      catMap[cat].totalScore += r.score
    })

    const getSectorLabel = (cat: string) => {
      if (cat === 'restaurant') return t('map.legendGastro', 'Restauración & Gastronomía')
      if (cat === 'chalet') return t('map.legendChalet', 'Cabañas & Glass Igloos')
      if (cat === 'attraction') return t('map.legendSafari', 'Safaris & Actividades')
      return t('map.legendDefault', 'Experiencia Turística')
    }

    const catBreakdown = Object.entries(catMap).map(([cat, data]) => ({
      name: cat,
      count: data.count,
      avgScore: +(data.totalScore / data.count).toFixed(2),
      pct: Math.round((data.count / total) * 100),
      color: SECTOR_THEME[cat]?.color || SECTOR_THEME.default.color,
      label: getSectorLabel(cat),
    }))

    // Mayor oportunidad (Top 5 con mayor score)
    const rankedOpportunities = [...recommendations]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)

    return {
      total,
      smeCount,
      smePct,
      coreCount,
      corePct,
      avgScore,
      digitalReadinessPct,
      catBreakdown,
      rankedOpportunities,
    }
  }, [pois, recommendations, t, poiTotal, poiSmeCount])

  // --- GRÁFICO 1: MIX DE CATEGORÍAS (DONUT POLAR) ---
  const donutChartOptions = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(11, 16, 33, 0.95)',
        borderColor: 'rgba(56, 189, 248, 0.35)',
        textStyle: { color: '#f1f5f9', fontSize: 12 },
        formatter: (params: any) => {
          return `<div style="font-weight:600; color:#38bdf8;">${params.name}</div>
                  <div style="font-size:11px; color:#cbd5e1; margin-top:2px;">
                    ${t('map.establishments', 'Establecimientos')}: <b>${params.value}</b> (${params.percent}%)
                  </div>`
        },
      },
      series: [
        {
          name: t('map.categoryMix', 'Categorías'),
          type: 'pie',
          radius: ['52%', '78%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#0b1021',
            borderWidth: 2,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            fontSize: 10,
            fontWeight: 'bold',
            color: '#ffffff',
          },
          data: marketStats.catBreakdown.map((c) => ({
            name: c.label,
            value: c.count,
            itemStyle: { color: c.color },
          })),
        },
      ],
    }
  }, [marketStats, t])

  // --- GRÁFICO 2: SCORE DE OPORTUNIDAD POR SECTOR (BARRAS HORIZONTALES) ---
  const barChartOptions = useMemo(() => {
    const sorted = [...marketStats.catBreakdown].sort((a, b) => a.avgScore - b.avgScore)
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(11, 16, 33, 0.95)',
        borderColor: 'rgba(56, 189, 248, 0.35)',
        textStyle: { color: '#f1f5f9', fontSize: 12 },
      },
      grid: { top: 10, right: 35, bottom: 20, left: 110 },
      xAxis: {
        type: 'value',
        min: 0,
        max: 1,
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.08)' } },
        axisLabel: {
          color: '#94a3b8',
          fontSize: 10,
          formatter: (v: number) => `${Math.round(v * 100)}%`,
        },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((c) => (c.label.length > 15 ? c.label.slice(0, 13) + '...' : c.label)),
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { color: '#cbd5e1', fontSize: 11, fontWeight: 500 },
      },
      series: [
        {
          type: 'bar',
          data: sorted.map((c) => ({
            value: c.avgScore,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: 'rgba(45, 212, 191, 0.3)' },
                { offset: 1, color: c.color },
              ]),
              borderRadius: [0, 6, 6, 0],
            },
          })),
          barWidth: 14,
          label: {
            show: true,
            position: 'right',
            color: '#38bdf8',
            fontSize: 10,
            fontFamily: 'monospace',
            formatter: (p: any) => `${(p.value * 100).toFixed(0)}%`,
          },
        },
      ],
    }
  }, [marketStats, t])

  return (
    <div className="space-y-6">
      {/* ========================================================================= */}
      {/* 1. CUADRO DE INTELIGENCIA DE MERCADO TURÍSTICO (KPIs & ANÁLISIS SECTORIAL) */}
      {/* ========================================================================= */}
      <Card className="border border-teal-500/25 bg-card shadow-xl overflow-hidden">
        <CardHeader className="pb-3 border-b bg-gradient-to-r from-card via-card/90 to-background">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400 border border-teal-500/20 shadow-inner">
                <BarChart3 className="size-4 text-teal-400" />
              </div>
              <div>
                <CardTitle className="text-base font-bold text-foreground flex items-center gap-2">
                  <span>{t('map.intelligenceTitle', 'Inteligencia de Oferta Turística de Levi')}</span>
                  <Badge variant="outline" className="text-[10px] text-teal-400 border-teal-500/30 font-mono">
                    {t('map.benchmarking', 'Benchmarking Local')}
                  </Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('map.intelligenceSub', 'Diagnóstico estratégico de oferta empresarial, distribución territorial y grado de madurez digital.')}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 bg-slate-950/60 space-y-4">
          {/* 4 KPIs de Alto Valor Estratégico */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* KPI 1: Negocios Analizados */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3.5 shadow-sm">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">{t('map.kpiActive', 'Establecimientos Activos')}</span>
                <Building2 className="size-4 text-teal-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-foreground">{marketStats.total}</div>
              <p className="text-[11px] text-teal-400/80 mt-0.5">{t('map.kpiActiveSub', 'Mapeados en Levi / Sirkka')}</p>
            </div>

            {/* KPI 2: % Pymes Locales */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3.5 shadow-sm">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">{t('map.kpiSmeWeight', 'Peso PYMES Locales')}</span>
                <ShieldCheck className="size-4 text-emerald-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-emerald-400">{marketStats.smePct}%</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{marketStats.smeCount} {t('map.kpiSmeSub', 'negocios independientes')}</p>
            </div>

            {/* KPI 3: % Núcleo Levi Core */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3.5 shadow-sm">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">{t('map.kpiCenterConc', 'Concentración Centro')}</span>
                <Target className="size-4 text-sky-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-sky-400">{marketStats.corePct}%</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{marketStats.coreCount} {t('map.kpiCenterSub', 'en Sirkka Keskus')}</p>
            </div>

            {/* KPI 4: Madurez Digital / Sitio Web */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3.5 shadow-sm">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">{t('map.kpiDigital', 'Madurez Digital')}</span>
                <Globe2 className="size-4 text-amber-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-amber-400">{marketStats.digitalReadinessPct}%</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{t('map.kpiDigitalSub', 'Con canal web propio auditable')}</p>
            </div>
          </div>

          {/* Gráficos de Benchmarking Sectorial */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            {/* Donut Mix de Categorías */}
            <div className="lg:col-span-4 rounded-xl border border-slate-800 bg-slate-900/70 p-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                    <PieChartIcon className="size-3.5 text-teal-400" />
                    {t('map.categoryMix', 'Mix de Categorías')}
                  </span>
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    {marketStats.catBreakdown.length} {t('map.sectors', 'sectores')}
                  </Badge>
                </div>
                <div className="h-44 w-full">
                  <ReactECharts option={donutChartOptions} style={{ height: '100%', width: '100%' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-slate-800/80">
                {marketStats.catBreakdown.slice(0, 4).map((c) => (
                  <div key={c.name} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className="size-2 rounded-full" style={{ backgroundColor: c.color }} />
                    <span className="truncate">{c.label}</span>
                    <span className="font-mono text-slate-300 ml-auto">{c.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Score Medio de Oportunidad por Sector */}
            <div className="lg:col-span-4 rounded-xl border border-slate-800 bg-slate-900/70 p-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                    <TrendingUp className="size-3.5 text-amber-400" />
                    {t('map.tractionScore', 'Score Medio de Tracción')}
                  </span>
                  <Badge variant="outline" className="text-[10px] text-amber-400/80 border-amber-500/30 font-mono">
                    {t('map.demand', 'Demanda')}
                  </Badge>
                </div>
                <div className="h-44 w-full">
                  <ReactECharts option={barChartOptions} style={{ height: '100%', width: '100%' }} />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground pt-2 border-t border-slate-800/80">
                {t('map.demandAlign', 'Alineación estimada con mercados emisores (Reino Unido, Alemania, Francia).')}
              </p>
            </div>

            {/* Top 5 Oportunidades Seleccionables */}
            <div className="lg:col-span-4 rounded-xl border border-slate-800 bg-slate-900/70 p-3 flex flex-col justify-between">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <Award className="size-3.5 text-teal-400" />
                  {t('map.campaignPotential', 'Mayor Potencial de Campaña')}
                </span>
                <Badge variant="outline" className="text-[10px] text-teal-400/80 border-teal-500/30 font-mono">
                  {t('map.top5', 'Top 5')}
                </Badge>
              </div>
              <div className="space-y-1.5 overflow-y-auto max-h-44 pr-1">
                {marketStats.rankedOpportunities.map((r, idx) => {
                  const isSelected = r.poi.poi_id === selectedPoiId
                  return (
                    <div
                      key={r.poi.poi_id}
                      onClick={() => onSelectPoi?.(r.poi)}
                      className={`cursor-pointer rounded-lg px-2.5 py-1.5 flex items-center justify-between text-xs transition-colors ${
                        isSelected
                          ? 'bg-amber-500/20 border border-amber-500/40 text-amber-200'
                          : 'bg-slate-800/40 hover:bg-slate-800/80 text-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-mono text-muted-foreground">#{idx + 1}</span>
                        <span className="truncate font-medium">{r.poi.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 font-mono text-[11px] text-amber-400">
                        <span>{(r.score * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
              <p className="text-[10px] text-muted-foreground pt-2 border-t border-slate-800/80 flex items-center gap-1">
                <Sparkles className="size-3 text-amber-400 shrink-0" />
                <span>{t('map.clickToFocus', 'Haz clic para enfocarlo en el mapa geográfico inferior.')}</span>
              </p>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-800">
            <ChartProvenanceCallout
              sourceName={t('chartExpl.poiOverview.sourceName')}
              sourceType="mixed"
              sourceDesc={t('chartExpl.poiOverview.sourceDesc')}
              insight={t('chartExpl.poiOverview.insight')}
            />
          </div>
        </CardContent>
      </Card>

      {/* ========================================================================= */}
      {/* 2. MAPA GEOGRÁFICO REAL EN MODO OSCURO (CARTOGRAFÍA + CAPA DE CALOR) */}
      {/* ========================================================================= */}
      <Card className="isolate relative z-0 border border-sky-500/25 bg-card shadow-2xl overflow-hidden">
        <CardHeader className="pb-3 border-b bg-gradient-to-r from-card via-card/90 to-background">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20 shadow-inner">
                <Compass className="size-4 text-sky-400" />
              </div>
              <div>
                <CardTitle className="text-base font-bold text-foreground flex items-center gap-2">
                  <span>{t('map.mapTitle', 'Mapa Geográfico Interactivo y Densidad de Oferta')}</span>
                  <Badge variant="outline" className="text-[10px] text-sky-400 border-sky-500/30 font-mono">
                    {t('map.mapBadge', 'Modo Oscuro · Levi Resort')}
                  </Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('map.mapSub', 'Cartografía real georreferenciada con marcadores sectoriales y halos de densidad/calor sobre el mismo terreno.')}
                </p>
              </div>
            </div>

            {/* Alternador de Capas Rápidas en el Mapa */}
            <div className="flex items-center gap-1 bg-slate-900/90 p-1 rounded-xl border border-slate-800">
              <Button
                size="sm"
                variant={activeLayer === 'all' ? 'default' : 'ghost'}
                className="text-xs h-7 px-2.5 flex items-center gap-1"
                onClick={() => setActiveLayer('all')}
              >
                <Layers className="size-3" />
                <span>{t('map.layerAll', 'Todo (Puntos + Calor)')}</span>
              </Button>
              <Button
                size="sm"
                variant={activeLayer === 'heatmap' ? 'default' : 'ghost'}
                className="text-xs h-7 px-2.5 flex items-center gap-1"
                onClick={() => setActiveLayer('heatmap')}
              >
                <Flame className="size-3" />
                <span>{t('map.layerHeat', 'Solo Densidad/Calor')}</span>
              </Button>
              <Button
                size="sm"
                variant={activeLayer === 'markers' ? 'default' : 'ghost'}
                className="text-xs h-7 px-2.5 flex items-center gap-1"
                onClick={() => setActiveLayer('markers')}
              >
                <MapPin className="size-3" />
                <span>{t('map.layerMarkers', 'Solo Marcadores')}</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 bg-slate-950">
          <div className="h-[520px] w-full relative">
            <MapContainer
              center={LEVI_CENTER}
              zoom={13}
              style={{ height: '100%', width: '100%', backgroundColor: '#090d16' }}
              scrollWheelZoom={true}
            >
              {/* Capa de mosaicos oscura libre y directa de Esri (No requiere API Key ni registro) */}
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />

              <MapFocusHandler selectedPoi={selectedPoi} />

              {/* CAPA DE CALOR / DENSIDAD REALIZADA CON HALOS GEOGRÁFICOS GRADIENTES */}
              {(activeLayer === 'all' || activeLayer === 'heatmap') &&
                recommendations.map((r) => {
                  const p = r.poi
                  // Radio de calor proporcional al score de tracción (40px a 90px de radio térmico)
                  const heatRadius = Math.max(30, Math.round(r.score * 75))
                  const heatOpacity = Math.max(0.12, r.score * 0.28)

                  return (
                    <CircleMarker
                      key={`heat-${p.poi_id}`}
                      center={[p.latitude, p.longitude]}
                      radius={heatRadius}
                      pathOptions={{
                        color: 'transparent',
                        fillColor: r.score > 0.7 ? '#f97316' : '#2dd4bf',
                        fillOpacity: heatOpacity,
                        weight: 0,
                      }}
                      interactive={false}
                    />
                  )
                })}

              {/* MARCADORES SECTORIALES PRECISOS */}
              {(activeLayer === 'all' || activeLayer === 'markers') &&
                recommendations.map((r) => {
                  const p = r.poi
                  const isSelected = p.poi_id === selectedPoiId
                  const sector = SECTOR_THEME[p.category] || SECTOR_THEME.default
                  const radius = isSelected ? 12 : Math.max(7, Math.round(r.score * 11))

                  const displayCat = p.category === 'restaurant'
                    ? t('map.legendGastro', 'Restauración')
                    : p.category === 'chalet'
                    ? t('map.legendChalet', 'Cabañas')
                    : p.category === 'attraction'
                    ? t('map.legendSafari', 'Safaris')
                    : (p.industry || t('map.legendDefault', 'Turismo'))

                  return (
                    <CircleMarker
                      key={p.poi_id}
                      center={[p.latitude, p.longitude]}
                      radius={radius}
                      pathOptions={{
                        color: isSelected ? '#ffffff' : sector.color,
                        fillColor: isSelected ? '#f59e0b' : sector.color,
                        fillOpacity: isSelected ? 0.95 : 0.85,
                        weight: isSelected ? 3 : 1.5,
                      }}
                      eventHandlers={{
                        click: () => onSelectPoi?.(p),
                      }}
                    >
                      <LeafletTooltip direction="top" offset={[0, -radius]} opacity={0.95}>
                        <div className="text-xs font-sans">
                          <div className="font-bold text-slate-100 flex items-center gap-1">
                            <span>{p.name}</span>
                            <span className="text-[10px] text-amber-400 font-mono">
                              {(r.score * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400">{displayCat}</div>
                        </div>
                      </LeafletTooltip>
                      <Popup>
                        <div className="p-1 min-w-[200px] font-sans text-xs">
                          <div className="font-bold text-sm text-slate-900 mb-1">{p.name}</div>
                          <div className="text-slate-600 mb-2">{displayCat}</div>
                          <div className="flex flex-wrap items-center gap-1.5 mb-2">
                            <span className="font-mono text-xs font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                              {t('map.scoreLabel', 'Score')} {Math.round(r.score * 100)}%
                            </span>
                            {p.is_sme && (
                              <span className="text-[10px] bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded font-medium">
                                {t('map.smeBadge', 'PYME')}
                              </span>
                            )}
                            {p.stars && <span className="text-amber-500 font-bold">{p.stars}★</span>}
                          </div>
                          {p.street_address && (
                            <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1">
                              <MapPin className="size-3" />
                              <span>{p.street_address}</span>
                            </div>
                          )}
                          {p.website && (
                            <a
                              href={p.website}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sky-600 font-medium hover:underline inline-flex items-center gap-1"
                            >
                              <span>{t('map.viewWebsite', 'Ver sitio web')}</span>
                              <ExternalLink className="size-3" />
                            </a>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  )
                })}
            </MapContainer>

            {/* Leyenda Geográfica Flotante */}
            <div className="absolute top-4 right-4 z-[1000] pointer-events-none rounded-xl bg-slate-900/90 p-3 text-xs text-slate-300 backdrop-blur-md border border-slate-800 shadow-2xl space-y-2">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                {t('map.legendSectors', 'Sectores & Densidad')}
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-orange-400 shadow-sm" />
                <span className="text-[11px]">{t('map.legendGastro', 'Gastronomía & Bares')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-sky-400 shadow-sm" />
                <span className="text-[11px]">{t('map.legendChalet', 'Cabañas & Glass Igloos')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-purple-400 shadow-sm" />
                <span className="text-[11px]">{t('map.legendSafari', 'Safaris & Actividades')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-3 rounded-full bg-gradient-to-r from-teal-400/40 to-orange-500/60" />
                <span className="text-[11px] text-slate-300">{t('map.legendHeat', 'Halos de Presión / Calor')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-amber-400 animate-ping" />
                <span className="text-[11px] font-semibold text-amber-300">{t('map.legendSelected', 'Seleccionado')}</span>
              </div>
            </div>

            {/* Badges inferiores de control */}
            <div className="absolute bottom-3 left-3 z-[1000] pointer-events-none rounded-lg bg-slate-900/85 px-3 py-1.5 text-[11px] text-slate-400 backdrop-blur-md border border-slate-800 flex items-center gap-2">
              <ZoomIn className="size-3.5 text-teal-400" />
              <span>{t('map.zoomNav', 'Rueda de ratón / Pellizco táctil para Zoom y Navegación')}</span>
            </div>

            <div className="absolute bottom-3 right-3 z-[1000] pointer-events-none rounded-lg bg-slate-900/85 px-3 py-1.5 text-[11px] text-slate-400 backdrop-blur-md border border-slate-800 flex items-center gap-1.5">
              <MapPin className="size-3 text-teal-400" />
              <span>{t('map.locationBadge', 'Sirkka · Kittilä (Finlandia)')}</span>
            </div>
          </div>

          {/* Tarjeta de detalle interactivo al seleccionar un POI */}
          {selectedPoi ? (
            <div className="m-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-slate-900 to-slate-900 p-3.5 text-xs text-slate-200 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <CheckCircle2 className="size-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-white">{selectedPoi.name}</span>
                    <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/40 font-mono">
                      {t('map.scoreLabel', 'Score')}: {selectedRec?.score.toFixed(2)}
                    </Badge>
                    {selectedPoi.is_sme && (
                      <Badge variant="outline" className="text-[9px] text-teal-400 border-teal-500/40">
                        {t('map.smeBadge', 'PYME')}
                      </Badge>
                    )}
                    {selectedPoi.in_levi_core && (
                      <Badge variant="outline" className="text-[9px] text-sky-400 border-sky-500/40">
                        {t('map.coreBadge', 'Levi Core')}
                      </Badge>
                    )}
                    {selectedPoi.stars ? (
                      <span className="text-amber-400 text-xs font-semibold flex items-center gap-0.5">
                        <Star className="size-3 fill-amber-400" />
                        {selectedPoi.stars}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {selectedPoi.street_address || selectedPoi.industry} · {selectedPoi.city || 'Levi'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedPoi.website ? (
                  <a
                    href={selectedPoi.website}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg px-3 py-1.5 font-medium flex items-center gap-1.5 transition-colors"
                  >
                    <ExternalLink className="size-3" />
                    {t('what.website', 'Sitio Web')}
                  </a>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="p-3 text-xs text-slate-400 border-t border-slate-800 bg-slate-900/60 flex items-center justify-between">
              <p className="flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-teal-400" />
                <span>{t('map.clickMapHelp', 'Haz clic en cualquier punto del mapa geográfico para ver la ficha detallada del negocio.')}</span>
              </p>
              <span className="font-mono text-[10px] text-slate-500">Esri ArcGIS Dark Canvas · Leaflet 1.9</span>
            </div>
          )}

          <div className="p-3 border-t border-slate-800 bg-slate-950">
            <ChartProvenanceCallout
              sourceName={t('chartExpl.poiMap.sourceName')}
              sourceType="real"
              sourceDesc={t('chartExpl.poiMap.sourceDesc')}
              insight={t('chartExpl.poiMap.insight')}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
